<script lang="ts" setup>
import { useNotificationPanelListCard } from './composables/useNotificationPanelListCard'

import type { Props } from './types'

import IconNotificationMessage from '@/assets/images/icon-notification-message.svg'
import IconNotificationDownload from '@/assets/images/icon-notification-download.svg'
import ABaseIcon from '@/components/atoms/ABaseIcon/index.vue'
import { useDate } from '@/composables/useDate'
import { useReadNotification } from '@/composables/api/customer/Notification'

/**
 * Props
 */
const props = withDefaults(defineProps<Props>(), {
  notificationMeta: undefined,
  isDetail: false,
})

/**
 * Emits
 */
type Emits = {
  (e: 'after-click'): void
  (e: 'refresh'): void
}
const emit = defineEmits<Emits>()

const { execute: readNotification } = useReadNotification(props.notificationMeta.id)

/**
 * Composables
 */
const {
  title,
  description,
  isIconMessage,
} = useNotificationPanelListCard(props)
const { formatDateTime } = useDate()

/**
 * Methods
 */
const onClickCard = async () => {
  if (props.notificationMeta.isRead) {
    emit('after-click')
    return
  }
  await readNotification()
  emit('refresh')
  emit('after-click')
}
</script>

<template>
  <div
    v-if="!props.isDetail"
    class="notification-panel-list-card"
    @click="onClickCard"
  >
    <div class="icon-container">
      <ABaseIcon
        :src="isIconMessage ? IconNotificationMessage : IconNotificationDownload"
        size="30px"
      />
    </div>
    <div class="content-container">
      <div class="title-container">
        <div
          v-if="!notificationMeta?.isRead"
          class="unread-icon-container"
        >
          <i class="unread-icon" />
        </div>
        <span class="title">{{ title }}</span>
      </div>
      <div class="date-container">
        {{ formatDateTime(props.notificationMeta?.createdAt) }}
      </div>
      <div class="description-container">
        {{ description }}
      </div>
    </div>
  </div>
  <div
    v-else
    class="notification-panel-list-card-detail"
  >
    <div class="title-container">
      <div class="icon-container">
        <ABaseIcon
          :src="isIconMessage ? IconNotificationMessage : IconNotificationDownload"
          size="30px"
        />
      </div>
      <div class="title">
        <span>{{ title }}</span>
      </div>
    </div>
    <div class="date-container">
      {{ formatDateTime(props.notificationMeta?.createdAt) }}
    </div>
    <div class="description-container">
      {{ description }}
    </div>
  </div>
</template>

<style scoped>
.notification-panel-list-card {
  display: flex;
  gap: 8px;
  width: 310px;
  min-height: 116px;
  padding: 8px;
  cursor: pointer;
  border: 1px solid rgb(var(--v-theme-ui-gray2));
  border-radius: 8px;
  box-shadow: 0 1px 2px 0 rgba(60, 60, 60, 0.24);

  .content-container {
    display: flex;
    flex-direction: column;
    gap: 4px;

    .title-container {
      display: flex;
      align-items: flex-start;

      .unread-icon-container {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 21px;
        height: 21px;
        padding: 5px;

        .unread-icon {
          width: 8px;
          height: 8px;
          background-color: rgb(var(--v-theme-ui-alert));
          border-radius: 50%;
        }
      }

      .title {
        display: flex;
        font-weight: 600;
        word-break: break-word;
      }
    }

    .date-container {
      font-size: 10px;
      color: rgb(var(--v-theme-ui-text-gray-1));
    }

    .description-container {
      margin-top: 4px;
      word-break: break-word;
      white-space: pre-line;
    }
  }

  &:hover {
    opacity: 0.7;
  }
}

.notification-panel-list-card-detail {
  display: flex;
  flex-direction: column;

  .title-container {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    height: 30px;

    .title {
      display: flex;
      align-items: center;
      height: 100%;
      font-size: 14px;
      font-weight: 600;
      word-break: break-word;
    }
  }

  .date-container {
    margin-top: 4px;
    font-size: 10px;
    color: rgb(var(--v-theme-ui-text-gray-1));
  }

  .description-container {
    margin-top: 24px;
    font-size: 14px;
    word-break: break-word;
    white-space: pre-line;
  }
}
</style>
